import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { ContentItem } from '@/common/types/lessonPlan';
import {
  Grapheme,
  GraphemeId,
  Morpheme,
  ScopeAndSequenceLevel,
  Sentence,
  SentenceId,
  Word,
  WordId,
} from '@/common/types/scopeAndSequence';
import {
  getGraphemeById,
  getGraphemesBySpelling,
  getMorphemesByIds,
  getSentenceById,
  getSentencesByIds,
  getWordById,
  getWordsByIds,
} from '@/common/utils/scopeAndSequence';
import { v4 as uuidv4 } from 'uuid';

export enum ActivityType {
  SOUND_RECOGNITION_DRILL = 'soundRecognitionDrill',
  LETTER_SOUND_SPELLING_DRILL = 'letterSoundSpellingDrill',
  ORAL_BLENDING_DRILL = 'oralBlendingDrill',
  ORAL_SEGMENTING_DRILL = 'oralSegmentingDrill',
  DECODING_WORD_CHAIN = 'decodingWordChain',
  WORD_READING = 'wordReading',
  SPELLING_DRILL = 'spellingDrill',
  SPELLING_WORD_CHAIN = 'spellingWordChain',
  WORD_SUMS = 'wordSums',
  SENTENCE_READING = 'sentenceReading',
  SENTENCE_DICTATION = 'sentenceDictation',
  WORD_MATRIX = 'wordMatrix',
}

const speakIcon = '/lesson_plan_icons/speak.png';
const whiteboardIcon = '/lesson_plan_icons/whiteboard.png';
// const countIcon = '/lesson_plan_icons/threefingers.png';
// const listenIcon = '/lesson_plan_icons/ear.png';

const escapeHtml = (str: string) => {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

export const activityDetails = {
  soundRecognitionDrill: {
    formatted: 'Sound Recognition Drill',
    instructions: [
      'You will point to the letter/letters and the learner will say the sound or sounds that the letter pattern represents.',
      'Move reasonably quickly, with minimal verbal input unless correcting an error.',
    ],
    skill: 'Skill: Letter-Sound Recognition',
    script:
      "I'm going to show you some letters, you say the sound or sounds they spell.",
    explainer:
      'This sound recognition drill supports learners with recalling common letter-sound correspondences. Fluent recognition of the sounds different letters or combinations of letters can spell helps quickly decoding new words.',
    icon: speakIcon,
    learnMoreUrl:
      'https://spellcaster.education/knowledge-base/activity-drills/sound-recognition-drill',
  },
  letterSoundSpellingDrill: {
    formatted: 'Letter Sound Spelling Drill',
    instructions: [
      'You will say sounds and the learner will write the spelling pattern/s for that sound.',
      'Spelling patterns are the letters used to spell sounds. Sometimes there is more than one way to spell a sound.',
      'For example ay, ai and a_e, a can spell the /ā/ (long-a sound) like in day, train, make, table.',
      'Learners should be encouraged to write all of the patterns they have learned for a sound.',
    ],
    skill: 'Skill: Letter-Sound Recognition',
    script:
      'I am going to say some sounds, write the letter or letters that spell the sounds I say.',
    explainer:
      'The Letter Sound Spelling Drill is designed to help learners practice recalling various spelling patterns that correspond with different phonemes and morphemes in our language. Fluent recall of various spelling patterns supports more efficent decoding and spelling.',
    icon: whiteboardIcon,
    learnMoreUrl:
      'https://spellcaster.education/knowledge-base/activity-drills/letter-sound-spelling-drill',
  },
  oralBlendingDrill: {
    formatted: 'Oral Blending Drill',
    instructions: [
      'Say the individual sounds for the first word in your list and ask the learner to blend them to make a word.',
      'For example, you say /m/ /ă/ /t/, they blend it to say mat.',
    ],
    skill: 'Skill: Phonemic Awareness',
    script: '"I am going to say the sounds in a word, then you say the word.".',
    explainer:
      'The oral blending drill supports phonemic awareness by practicing blending isolated sounds into words. This skill, particularly blending sounds together, is crucial for decoding words efficiently.',
    icon: speakIcon,
    learnMoreUrl:
      'https://spellcaster.education/knowledge-base/activity-drills/oral-blending-drill',
  },
  oralSegmentingDrill: {
    formatted: 'Oral Segmenting Drill',
    instructions: [
      'Say the first word in your list and ask the learner to segment it into its sounds, using finger spelling if necessary.',
      'For example, you say mat, they segment it into /m/ /ă/ /t/.',
    ],
    skill: 'Skill: Phonemic Awareness',
    script:
      '"I am going to say a word, I want you to break/finger-spell it into its sounds."',
    explainer:
      'The oral segmenting drill supports phonemic awareness and spelling by practicing segmenting words into their constituent sounds. This skill is very useful for spelling words accurately. If appropriate, learners can finger spell the word for an additional scaffold.',
    icon: speakIcon,
    learnMoreUrl:
      'https://spellcaster.education/knowledge-base/activity-drills/oral-segmenting-drill',
  },
  sentenceReading: {
    formatted: 'Sentence Reading Drill',
    instructions: [
      'Show the learner(s) the sentence and ask them to read it aloud.',
      'If necessary have them read it word by word and then fluently.',
    ],
    skill: 'Skill: Fluency & Decoding',
    script: '"I am going to show you some sentences to read."',
    explainer:
      'Sentence reading drills help learners to apply their decoding skills in more authentic scenarios.',
    icon: speakIcon,
    learnMoreUrl:
      'https://spellcaster.education/knowledge-base/activity-drills/sentence-reading-drill',
  },
  sentenceDictation: {
    formatted: 'Sentence Dictation Drill',
    instructions: [
      'Dictate the sentence.',
      'Have the learner repeat the sentence back to you and then write it.',
      'Have the learner check their sentence with a green pen for capital letters, punctuation, and correct spelling.',
      'Follow a routine of ticking the capital letter, ticking the punctuation.',
    ],
    skill: 'Skill: Fluency & Spelling',
    script:
      'I am going to read out a sentence, say it back to me and then write it down.',
    explainer:
      'The Sentence Dictation Drill supports learners to spell words in common scenarios - writing sentences. Sentence dictation is a way to practice spelling skills as well as sentence construction skills such as capital letters and punctuation. Sentence dictation is also helpful for phonics and phonemic awareness.',
    icon: whiteboardIcon,
    learnMoreUrl:
      'https://spellcaster.education/knowledge-base/activity-drills/sentence-dictation-drill',
  },
  wordSums: {
    formatted: 'Word Sums Drill',
    instructions: [
      'Show the learner a word broken into its morphological units in the form of a sum.',
      'Have the learner sum up the word, applying the appropriate suffixing conventions where necessary.',
    ],
    skill: 'Skill: Morphological Awareness',
    script:
      "You're going to spell some words using prefixes and suffixes. Remember to use the appropriate suffixing conventions where necessary.",
    explainer:
      'The Word Sums drill supports morphological awareness. Morphology is a powerful way to boost spelling of multisyllabic words.',
    icon: whiteboardIcon,
    learnMoreUrl:
      'https://spellcaster.education/knowledge-base/activity-drills/word-sums-drill',
  },
  decodingWordChain: {
    formatted: 'Decoding Word Chain Drill',
    instructions: [
      'Build the first word using the Spellcsater word mat or your own manipulables, have the learner read the word.',
      'Swap a spelling to make a new word, have the learner read the new word etc.',
    ],
    skill: 'Skill: Decoding',
    script: 'I am going to change a sound in the word, then you read the word.',
    explainer:
      'The Decoding Word Chain Drill supports phonemic awareness and phonics, by having them read a succession of words while changing one sound at a time.',
    icon: speakIcon,
    learnMoreUrl:
      'https://spellcaster.education/knowledge-base/activity-drills/decoding-word-chain-drill',
  },
  spellingWordChain: {
    formatted: 'Spelling Word Chain Drill',
    instructions: [
      'Set up your magnetic letters on a whiteboard (or use the Spellcaster wordmat).',
      'Say the first word in the chain, have the learner spell the word.',
      'Say the next word in the chain, have the learner change the spelling of the word to spell the next word etc.',
    ],
    skill: 'Skill: Encoding (Spelling)',
    script: 'Turn [word 1] into [word 2], turn [word 2] into [word 3] etc.',
    explainer:
      'The Spelling Word Chain drill helps learners to practice their spelling, reinforces target letter-sound patterns and also supports phonemic awareness. Spelling is a powerful lever for both reading and writing.',
    icon: whiteboardIcon,
    learnMoreUrl:
      'https://spellcaster.education/knowledge-base/activity-drills/spelling-word-chain-drill',
  },
  wordReading: {
    formatted: 'Word Reading Drill',
    instructions: [
      'Show the learner the first word in the list, ask them to read it.',
      'Repeat with the next word in the list, etc.',
    ],
    skill: 'Skill: Decoding (Reading)',
    script:
      'We are going to read some words where [insert spelling] makes the [insert sound] (eg. "ay" makes the /ā/ sound).',
    explainer:
      'The Word Reading drill helps learners practice decoding a series of words that contain a series of target letter-sound combinations.',
    icon: speakIcon,
    learnMoreUrl:
      'https://spellcaster.education/knowledge-base/activity-drills/word-reading-drill',
  },
  spellingDrill: {
    formatted: 'Spelling Drill',
    instructions: [
      'Say the first word in your list.',
      'The learner writes the word (if appropriate they can finger spell it as well.).',
      'Repeat with the next word in your list etc.',
    ],
    skill: 'Skill: Encoding (Spelling)',
    script:
      'We are are going to read some words where [insert spelling] makes the [insert sound] (eg. "ew" makes the /ʊ/ sound like in "moon").',
    explainer:
      'The spelling drill focuses on spelling a series of words which contain target letter-sound, or phoneme-grapheme grapheme, patterns.',
    icon: whiteboardIcon,
    learnMoreUrl:
      'https://spellcaster.education/knowledge-base/activity-drills/spelling-drill',
  },
  wordMatrix: {
    formatted: 'Word Matrix',
    instructions: [
      'Show the learner the base word in the centre of the web.',
      'Have the learner combine each prefix and/or suffix with the base word to make as many real words as they can.',
      'Discuss the meaning of each new word formed.',
    ],
    skill: 'Skill: Morphological Awareness',
    script:
      'Look at the base word in the middle. Use the prefixes and suffixes to make as many real words as you can.',
    explainer:
      'The Word Matrix activity supports morphological awareness by having learners combine prefixes and suffixes with a base word to form new words. This builds understanding of how morphemes change word meaning.',
    icon: whiteboardIcon,
    learnMoreUrl:
      'https://spellcaster.education/knowledge-base/activity-drills/word-matrix',
  },
};

export const fonts = [
  { name: 'Helvetica', value: 'inherit' },
  { name: 'Handwriting 1', value: 'NSWCursive' },
  { name: 'Handwriting 2', value: 'QLDCursive' },
  { name: 'Handwriting 3', value: 'SACursive' },
  { name: 'Handwriting 4', value: 'TASCursive' },
  { name: 'Handwriting 5', value: 'Aronui' },
  { name: 'Comic Sans', value: 'ComicSansMS' },
  { name: 'Courier', value: 'CourierNew' },
  { name: 'Georgia', value: 'Georgia' },
  { name: 'Times', value: 'TimesNewRoman' },
  { name: 'Verdana', value: 'Verdana' },
];

export const fontSizes = [
  { name: 'Default', value: 'inherit' },
  { name: 'Small', value: '12px' },
  { name: 'Normal', value: '16px' },
  { name: 'Large', value: '24px' },
  { name: 'Extra Large', value: '32px' },
];

/**
 *
 * @param type
 * @returns
 */
export function getActivityTitle(type: ActivityType) {
  return activityDetails[type].formatted;
}
/**
 * Retrieves the instruction for an activity type.
 * @param type - The type of activity.
 * @returns The instruction for the activity type as an array of bullet points.
 */
export function getActivityInstruction(type: ActivityType) {
  return activityDetails[type].instructions;
}

/**
 * Renders instructions as an HTML bullet list.
 * @param instructions - Array of instruction strings.
 * @returns HTML string with bullet list.
 */
export function renderInstructionsAsBulletList(instructions: string[]): string {
  if (instructions.length === 0) return '';
  const listItems = instructions
    .map(
      (instruction) =>
        `<li class="instruction-item">${escapeHtml(instruction)}</li>`,
    )
    .join('');
  return `<ul class="instructions-list">${listItems}</ul>`;
}

/**
 * Retrieves the script for an activity type.
 * @param type - The type of activity.
 * @returns The script for the activity type.
 */
export function getActivityScript(type: ActivityType) {
  return activityDetails[type].script;
}
/**
 * Retrieves the skill for an activity type.
 * @param type - The type of activity.
 * @returns The skill for the activity type.
 */
export function getActivitySkill(type: ActivityType) {
  return activityDetails[type].skill;
}

/**
 * Retrieves the formatted version of an activity type.
 * @param type - The type of activity.
 * @returns The formatted version of the activity type.
 */
export function getFormattedActivity(type: ActivityType) {
  return activityDetails[type].formatted;
}

/**
 * Retrieves the explainer for an activity type.
 * @param type - The type of activity.
 * @returns The explainer for the activity type.
 */
export function getActivityExplainer(type: ActivityType) {
  return activityDetails[type].explainer;
}

/**
 * Retrieves the URL for the learn more page for an activity type.
 * @param type - The type of activity.
 * @returns The URL for the documentation page for the activity type.
 */
export function getActivityLearnMoreUrl(type: ActivityType) {
  return activityDetails[type].learnMoreUrl;
}

/**
 * Creates a ContentItem object based on the type and item provided.
 * @param activityType - The type of activity.
 * @param item - The item to be used in the activity.
 * @returns A ContentItem object.
 */
export function contentItemFactory(
  activityType: ActivityType,
  item: Grapheme | Word | Word[] | Sentence | Morpheme | Morpheme[],
): ContentItem {
  const newContentItem: ContentItem = {
    id: uuidv4(),
    value: '',
  };
  switch (activityType) {
    case 'soundRecognitionDrill':
      newContentItem.value = (item as Grapheme).pronunciation
        ? `${(item as Grapheme).spelling}|${(item as Grapheme).pronunciation}`
        : (item as Grapheme).spelling;
      break;

    case 'letterSoundSpellingDrill':
      const grapheme = item as Grapheme;

      newContentItem.value = `${grapheme.humanFriendly} <${grapheme.spelling}>`;
      break;

    case 'oralSegmentingDrill':
      newContentItem.value = (item as Word).spelling;
      break;

    case 'oralBlendingDrill':
      newContentItem.value = (item as Word).spelling;
      break;

    case 'decodingWordChain':
      const decodingWords = item as Word[];
      newContentItem.value = decodingWords.map((word) => word.spelling);
      break;

    case 'spellingWordChain':
      const spellingWords = item as Word[];
      newContentItem.value = spellingWords.map((word) => word.spelling);
      break;

    case 'sentenceReading':
      newContentItem.value = (item as Sentence).spelling;
      break;

    case 'wordSums':
      const wordSums = item as Word;
      const morphemeStrings = getMorphemesByIds(wordSums.morphemes).map(
        (morpheme) => morpheme.spelling,
      );
      newContentItem.value = `${morphemeStrings.join(' + ')} = ${
        wordSums.spelling
      }`;
      break;

    case 'sentenceDictation':
      newContentItem.value = (item as Sentence).spelling;
      break;

    case 'wordReading':
      newContentItem.value = (item as Word).spelling;
      break;

    case 'spellingDrill':
      newContentItem.value = (item as Word).spelling;
      break;
  }

  return newContentItem;
}

/**
 * A record of content items for each activity type.
 */
export const contentItemBank = (() => {
  const contentBank: Record<ActivityType, ContentItem[][]> = {
    soundRecognitionDrill: [],
    letterSoundSpellingDrill: [],
    oralSegmentingDrill: [],
    oralBlendingDrill: [],
    sentenceReading: [],
    sentenceDictation: [],
    wordSums: [],
    decodingWordChain: [],
    spellingWordChain: [],
    wordReading: [],
    spellingDrill: [],
    wordMatrix: [],
  };

  const levels = scopeAndSequence.levels as ScopeAndSequenceLevel[];

  levels.forEach((levelData, i) => {
    if (!contentBank.soundRecognitionDrill[i])
      contentBank.soundRecognitionDrill[i] = [];
    if (!contentBank.letterSoundSpellingDrill[i])
      contentBank.letterSoundSpellingDrill[i] = [];
    if (!contentBank.oralSegmentingDrill[i])
      contentBank.oralSegmentingDrill[i] = [];
    if (!contentBank.sentenceReading[i]) contentBank.sentenceReading[i] = [];
    if (!contentBank.sentenceDictation[i])
      contentBank.sentenceDictation[i] = [];
    if (!contentBank.wordSums[i]) contentBank.wordSums[i] = [];
    if (!contentBank.decodingWordChain[i])
      contentBank.decodingWordChain[i] = [];
    if (!contentBank.spellingWordChain[i])
      contentBank.spellingWordChain[i] = [];
    if (!contentBank.wordReading[i]) contentBank.wordReading[i] = [];
    if (!contentBank.spellingDrill[i]) contentBank.spellingDrill[i] = [];
    if (!contentBank.oralBlendingDrill[i])
      contentBank.oralBlendingDrill[i] = [];

    contentBank.soundRecognitionDrill[i].push(
      ...levelData.newGraphemes
        .map((graphemeId: GraphemeId) => {
          const grapheme = getGraphemeById(graphemeId);
          if (!grapheme) return null;
          return contentItemFactory(
            ActivityType.SOUND_RECOGNITION_DRILL,
            grapheme,
          );
        })
        .filter((g) => !!g),
    );
    contentBank.letterSoundSpellingDrill[i].push(
      ...levelData.newGraphemes
        .map((graphemeId: GraphemeId) => {
          const grapheme = getGraphemeById(graphemeId);
          if (!grapheme) return null;
          return contentItemFactory(
            ActivityType.LETTER_SOUND_SPELLING_DRILL,
            grapheme,
          );
        })
        .filter((g) => !!g),
    );
    contentBank.oralSegmentingDrill[i].push(
      ...levelData.wordSet
        .map((wordId: WordId) => {
          const word = getWordById(wordId);
          if (!word) return null;
          return contentItemFactory(ActivityType.ORAL_SEGMENTING_DRILL, word);
        })
        .filter((g) => !!g),
    );
    contentBank.oralBlendingDrill[i].push(
      ...levelData.wordSet
        .map((wordId: WordId) => {
          const word = getWordById(wordId);
          if (!word) return null;
          return contentItemFactory(ActivityType.ORAL_BLENDING_DRILL, word);
        })
        .filter((g) => !!g),
    );
    contentBank.wordReading[i].push(
      ...levelData.wordSet
        .map((wordId: WordId) => {
          const word = getWordById(wordId);
          if (!word) return null;
          return contentItemFactory(ActivityType.WORD_READING, word);
        })
        .filter((g) => !!g),
    );
    contentBank.spellingDrill[i].push(
      ...levelData.wordSet
        .map((wordId: WordId) => {
          const word = getWordById(wordId);
          if (!word) return null;
          return contentItemFactory(ActivityType.SPELLING_DRILL, word);
        })
        .filter((g) => !!g),
    );

    if (levelData.wordChain.length) {
      const wordChain = getWordsByIds(levelData.wordChain);
      contentBank.spellingWordChain[i].push(
        contentItemFactory(ActivityType.SPELLING_WORD_CHAIN, wordChain),
      );
      contentBank.decodingWordChain[i].push(
        contentItemFactory(ActivityType.DECODING_WORD_CHAIN, wordChain),
      );
    }

    if (levelData.sentences) {
      const sentences = getSentencesByIds(levelData.sentences);
      contentBank.sentenceReading[i].push(
        ...sentences.map((sentence: Sentence) =>
          contentItemFactory(ActivityType.SENTENCE_READING, sentence),
        ),
      );
      contentBank.sentenceDictation[i].push(
        ...sentences.map((sentence: Sentence) =>
          contentItemFactory(ActivityType.SENTENCE_DICTATION, sentence),
        ),
      );
    }

    if (levelData.wordSet) {
      const words = getWordsByIds(levelData.wordSet);
      contentBank.wordSums[i].push(
        ...words.map((word: Word) =>
          contentItemFactory(ActivityType.WORD_SUMS, word),
        ),
      );
      contentBank.wordReading[i].push(
        ...words.map((word: Word) =>
          contentItemFactory(ActivityType.WORD_READING, word),
        ),
      );
      contentBank.spellingDrill[i].push(
        ...words.map((word: Word) =>
          contentItemFactory(ActivityType.SPELLING_DRILL, word),
        ),
      );
    }
  });

  return contentBank;
})();

function getContentBankItem(value: string | string[]) {
  for (const type in contentItemBank) {
    for (const level of contentItemBank[type as ActivityType]) {
      for (const item of level) {
        if (
          // Handle string values
          typeof item.value === 'string' &&
          typeof value === 'string' &&
          item.value === value
        ) {
          return item;
        } else if (
          // Handle array values (e.g. soundSwapping)
          Array.isArray(item.value) &&
          Array.isArray(value) &&
          item.value.join('') === value.join('')
        ) {
          return item;
        }
      }
    }
  }
}

/**
 * Creates an Activity object based on the type and level data provided.
 * @param type - The type of activity.
 * @param levelData - The level data to be used in the activity.
 * @returns An Activity object.
 */
export function activityFactory(
  type: ActivityType,
  levelData?: ScopeAndSequenceLevel,
  isReviewSection: boolean = false,
) {
  const newActivity: Section = {
    id: uuidv4(),
    type,
    title: '',
    description: getActivityInstruction(type).join(' '),
    content: [],
    additionalContent: '',
  };

  if (!levelData) {
    // Return a blank activity if no levelData is provided
    return newActivity;
  }

  const currentLevelIndex = scopeAndSequence.levels.findIndex(
    (level) => level.level === levelData.level,
  );

  // Helper function to get previous level's word sets
  const getPreviousLevelWordSet = (levelsBack: number): WordId[] => {
    const targetIndex = currentLevelIndex - levelsBack;
    if (targetIndex >= 0) {
      return scopeAndSequence.levels[targetIndex].wordSet as WordId[];
    }
    return [];
  };

  switch (type) {
    case 'soundRecognitionDrill':
      newActivity.title = 'Sound Recognition Drill';

      if (isReviewSection) {
        // Review: collect graphemes from preceding levels only (not current)
        const reviewGraphemes: GraphemeId[] = [];
        for (
          let i = currentLevelIndex - 1;
          i >= 0 && reviewGraphemes.length < 15;
          i--
        ) {
          const previousLevel = scopeAndSequence.levels[i];
          reviewGraphemes.unshift(
            ...(previousLevel.newGraphemes.slice(-15) as GraphemeId[]),
          );

          if (reviewGraphemes.length > 15) {
            reviewGraphemes.splice(0, reviewGraphemes.length - 15);
          }
        }

        reviewGraphemes.forEach((graphemeId: GraphemeId) => {
          const grapheme = getGraphemeById(graphemeId);
          if (!grapheme) return;
          const item = getContentBankItem(
            contentItemFactory(type, grapheme).value,
          );
          if (item) newActivity.content.push(item);
        });
      } else {
        // New content: only show current level's new graphemes
        levelData.newGraphemes.forEach((graphemeId: GraphemeId) => {
          const grapheme = getGraphemeById(graphemeId);
          if (!grapheme) return;
          const item = getContentBankItem(
            contentItemFactory(type, grapheme).value,
          );
          if (item) newActivity.content.push(item);
        });
      }
      break;

    case 'letterSoundSpellingDrill':
      newActivity.title = 'Letter Sound Spelling Drill';

      if (isReviewSection) {
        // Review: collect graphemes from preceding levels only (not current)
        const reviewGraphemesAuditory: GraphemeId[] = [];
        for (
          let i = currentLevelIndex - 1;
          i >= 0 && reviewGraphemesAuditory.length < 10;
          i--
        ) {
          const previousLevel = scopeAndSequence.levels[i];
          reviewGraphemesAuditory.unshift(
            ...(previousLevel.newGraphemes.slice(-10) as GraphemeId[]),
          );

          if (reviewGraphemesAuditory.length > 10) {
            reviewGraphemesAuditory.splice(
              0,
              reviewGraphemesAuditory.length - 10,
            );
          }
        }

        reviewGraphemesAuditory.forEach((graphemeId: GraphemeId) => {
          const grapheme = getGraphemeById(graphemeId);
          if (!grapheme) return;
          const item = getContentBankItem(
            contentItemFactory(type, grapheme).value,
          );
          if (item) newActivity.content.push(item);
        });
      } else {
        // New content: only show current level's new graphemes
        levelData.newGraphemes.forEach((graphemeId: GraphemeId) => {
          const grapheme = getGraphemeById(graphemeId);
          if (!grapheme) return;
          const item = getContentBankItem(
            contentItemFactory(type, grapheme).value,
          );
          if (item) newActivity.content.push(item);
        });
      }
      break;

    case 'oralSegmentingDrill':
      newActivity.title = 'Oral Segmenting Drill';

      if (isReviewSection) {
        // For review section, get words from 2 levels back
        const levelTwoBack = currentLevelIndex - 2;
        if (levelTwoBack >= 0) {
          const previousLevelWordSet = scopeAndSequence.levels[levelTwoBack]
            .wordSet as WordId[];

          // Shuffle and pick 5 random words from two levels back
          const shuffledWordSet = previousLevelWordSet.sort(
            () => 0.5 - Math.random(),
          );
          const selectedWords = shuffledWordSet.slice(0, 5);

          // Create content items for the selected words
          selectedWords.forEach((wordId: WordId) => {
            const word = getWordById(wordId);
            if (!word) return;
            const item = getContentBankItem(
              contentItemFactory(type, word).value,
            );
            if (item) newActivity.content.push(item);
          });
        }
      } else {
        // For new content section, use current level words
        if (levelData.wordSet) {
          // Use 3-4 words from current level
          levelData.wordSet.slice(0, 4).forEach((wordId: WordId) => {
            const word = getWordById(wordId);
            if (!word) return;
            const item = getContentBankItem(
              contentItemFactory(type, word).value,
            );
            if (item) newActivity.content.push(item);
          });
        }
      }
      break;

    case 'wordReading':
      newActivity.title = 'Word Reading';

      // Get 3-4 words from current level's word sets
      if (levelData.wordSet) {
        levelData.wordSet.slice(0, 4).forEach((wordId: WordId) => {
          const word = getWordById(wordId);
          if (!word) return;
          const item = getContentBankItem(
            contentItemFactory(ActivityType.WORD_READING, word).value,
          );
          if (item) newActivity.content.push(item);
        });
      }
      break;

    case 'spellingDrill':
      newActivity.title = 'Spelling Drill';

      if (isReviewSection) {
        // Use words from previous level for review section
        const previousLevelWordSets = getPreviousLevelWordSet(1);
        if (previousLevelWordSets.length > 0) {
          // Shuffle and pick 4-5 words from the previous level's word set
          const shuffledWordSet = previousLevelWordSets.sort(
            () => 0.5 - Math.random(),
          );
          const selectedWords = shuffledWordSet.slice(0, 5);

          // Create content items for the selected words
          selectedWords.forEach((wordId: WordId) => {
            const word = getWordById(wordId);
            if (!word) return;
            const item = getContentBankItem(
              contentItemFactory(ActivityType.ORAL_SEGMENTING_DRILL, word)
                .value,
            );
            if (item) newActivity.content.push(item);
          });
        }
      } else {
        // Use words from current level for new content section
        if (levelData.wordSet) {
          levelData.wordSet.slice(0, 5).forEach((wordId: WordId) => {
            const word = getWordById(wordId);
            if (!word) return;
            const item = getContentBankItem(
              contentItemFactory(ActivityType.ORAL_SEGMENTING_DRILL, word)
                .value,
            );
            if (item) newActivity.content.push(item);
          });
        }
      }
      break;

    case 'decodingWordChain':
      newActivity.title = 'Decoding Word Chain';

      // Get the word chain from the preceding level
      const precedingLevelIndexForWordChain = currentLevelIndex - 1;
      if (precedingLevelIndexForWordChain >= 0) {
        const precedingLevelWordChain = scopeAndSequence.levels[
          precedingLevelIndexForWordChain
        ].wordChain as WordId[];

        // Create content item for the word chain
        const decodingChain = getContentBankItem(
          contentItemFactory(type, getWordsByIds(precedingLevelWordChain))
            .value,
        );
        if (decodingChain) newActivity.content.push(decodingChain);
      }
      break;

    case 'spellingWordChain':
      newActivity.title = 'Spelling Word Chain';
      const spellingChain = getContentBankItem(
        contentItemFactory(type, getWordsByIds(levelData.wordChain)).value,
      );
      if (spellingChain) newActivity.content.push(spellingChain);

      break;

    case 'sentenceReading':
      newActivity.title = 'Sentence Reading';
      if (levelData.sentences) {
        levelData.sentences.forEach((sentenceId: SentenceId) => {
          const sentence = getSentenceById(sentenceId);
          if (!sentence) return;
          const item = getContentBankItem(
            contentItemFactory(type, sentence).value,
          );
          if (item) newActivity.content.push(item);
        });
      }
      break;

    case 'sentenceDictation':
      newActivity.title = 'Sentence Dictation';
      if (levelData.sentences) {
        levelData.sentences.forEach((sentenceId: SentenceId) => {
          const sentence = getSentenceById(sentenceId);
          if (!sentence) return;
          const item = getContentBankItem(
            contentItemFactory(type, sentence).value,
          );
          if (item) newActivity.content.push(item);
        });
      }
      break;

    case 'wordSums':
      newActivity.title = 'Word Sums';
      if (levelData.wordSet) {
        levelData.wordSet.forEach((wordId: WordId) => {
          const word = getWordById(wordId);
          if (!word) return;
          const item = getContentBankItem(contentItemFactory(type, word).value);
          if (item) newActivity.content.push(item);
        });
      }
      break;

    case 'wordMatrix':
      newActivity.title = 'Word Matrix';
      break;
  }

  return newActivity;
}

// /**
//  * Determines if a section is an activity.
//  * @param section - The section to check.
//  * @returns True if the section is an Activity, false otherwise.
//  */
// export function isActivity(section: Section): section is Section {
//   return Object.values(ActivityType).includes(section.type);
// }

export function extractWordFromPhonemeString(phonemeString: string): string {
  const match = phonemeString.match(/\(([^)]+)\)/);
  return match ? match[1] : phonemeString;
}
export function parseHTMLToSections(html: string): Section[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const sections: Section[] = [];

  function elementToString(element: Element): string {
    return element.outerHTML;
  }

  function cleanHtmlContent(html: string): string {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  }

  function parseActivityBlock(node: HTMLElement) {
    const id = node.getAttribute('data-id');
    const type = node.getAttribute('data-type') as ActivityType;
    const title = node.querySelector('h3')?.textContent || '';

    // Handle Word Matrix content separately
    const wordMatrixContainer = node.querySelector('.word-matrix-content');
    let content: ContentItem[];
    if (type === ActivityType.WORD_MATRIX && wordMatrixContainer) {
      content = [];
      // Read from data attributes first (TipTap serializes these)
      const attrBaseWord = wordMatrixContainer.getAttribute('data-base-word');
      const attrPrefixCols = wordMatrixContainer.getAttribute(
        'data-prefix-columns',
      );
      const attrSuffixCols = wordMatrixContainer.getAttribute(
        'data-suffix-columns',
      );

      if (
        attrBaseWord !== null ||
        attrPrefixCols !== null ||
        attrSuffixCols !== null
      ) {
        if (attrBaseWord) {
          content.push({ id: uuidv4(), value: `base:${attrBaseWord}` });
        }
        if (attrPrefixCols) {
          content.push({
            id: uuidv4(),
            value: `prefixColumns:${attrPrefixCols}`,
          });
        }
        if (attrSuffixCols) {
          content.push({
            id: uuidv4(),
            value: `suffixColumns:${attrSuffixCols}`,
          });
        }
      } else {
        // Legacy: check old data-prefixes/data-suffixes attrs
        const legacyPrefixes =
          wordMatrixContainer.getAttribute('data-prefixes');
        const legacySuffixes =
          wordMatrixContainer.getAttribute('data-suffixes');
        if (legacyPrefixes !== null || legacySuffixes !== null) {
          const baseWord = wordMatrixContainer.getAttribute('data-base-word');
          if (baseWord) {
            content.push({ id: uuidv4(), value: `base:${baseWord}` });
          }
          if (legacyPrefixes) {
            content.push({
              id: uuidv4(),
              value: `prefixColumns:${legacyPrefixes}`,
            });
          }
          if (legacySuffixes) {
            content.push({
              id: uuidv4(),
              value: `suffixColumns:${legacySuffixes}`,
            });
          }
        } else {
          // Fall back to child element text content
          const baseEl =
            wordMatrixContainer.querySelector('.word-matrix-base') ||
            wordMatrixContainer.querySelector('.base-word');
          if (baseEl && baseEl.textContent) {
            content.push({ id: uuidv4(), value: `base:${baseEl.textContent}` });
          }
          const prefixCols = Array.from(
            wordMatrixContainer.querySelectorAll('.word-matrix-prefix-col'),
          );
          if (prefixCols.length > 0) {
            const serialized = prefixCols
              .map((col) =>
                Array.from(col.querySelectorAll('.prefix'))
                  .map((el) => el.textContent || '')
                  .filter(Boolean)
                  .join(','),
              )
              .join('|');
            if (serialized)
              content.push({
                id: uuidv4(),
                value: `prefixColumns:${serialized}`,
              });
          }
          const suffixCols = Array.from(
            wordMatrixContainer.querySelectorAll('.word-matrix-suffix-col'),
          );
          if (suffixCols.length > 0) {
            const serialized = suffixCols
              .map((col) =>
                Array.from(col.querySelectorAll('.suffix'))
                  .map((el) => el.textContent || '')
                  .filter(Boolean)
                  .join(','),
              )
              .join('|');
            if (serialized)
              content.push({
                id: uuidv4(),
                value: `suffixColumns:${serialized}`,
              });
          }
        }
      }
    } else {
      // Get activity content items — search the whole block because
      // there are two .activity-content paragraphs (label + items)
      content = Array.from(node.querySelectorAll('.content-item')).map(
        (item) => {
          const text = cleanHtmlContent(elementToString(item));
          const pronunciation = item.getAttribute('data-pronunciation');
          if (pronunciation) {
            // Text may be "spelling for exampleWord" — extract just the spelling
            const forIdx = text.indexOf(' for ');
            const spelling = forIdx >= 0 ? text.substring(0, forIdx) : text;
            return { id: uuidv4(), value: `${spelling}|${pronunciation}` };
          }
          return { id: uuidv4(), value: text };
        },
      );
    }

    // Get activity metadata
    const skill = cleanHtmlContent(
      node.querySelector('.skill')?.textContent || '',
    );
    // Parse instructions - preserve HTML structure for bullet lists
    const instructionsElement = node.querySelector('.instructions');
    let instructions = '';
    if (instructionsElement) {
      const ulElement = instructionsElement.querySelector('ul');
      if (ulElement) {
        // Extract the HTML of the ul element (preserves bullet list structure)
        instructions = ulElement.outerHTML;
      } else {
        // Fallback to text content if no ul found (backward compatibility)
        instructions = cleanHtmlContent(instructionsElement.textContent || '');
      }
    }
    const script = cleanHtmlContent(
      node.querySelector('.script')?.textContent || '',
    );
    const additionalNotes = Array.from(
      node.querySelectorAll('.additional-notes') || [],
    )
      .map((note) => cleanHtmlContent(note.textContent || ''))
      .join('\n');

    sections.push({
      id,
      type,
      title,
      description: '',
      content,
      additionalContent: additionalNotes,
      skill,
      instructions,
      script,
    });
  }

  function processNode(node: ChildNode) {
    if (!(node instanceof HTMLElement)) return;

    if (node.classList.contains('lesson-guidance-block')) {
      // Parse lesson guidance block
      const levelId = node.getAttribute('data-type');
      const title = node.querySelector('h1')?.textContent || '';
      const info = node.querySelector('p')?.textContent || '';
      const codeList =
        node.querySelector('.horizontal-list')?.textContent || '';

      sections.push({
        id: levelId,
        type: 'lessonGuidance',
        title,
        description: '',
        content: [
          {
            id: 'levelInfo',
            value: info,
          },
          {
            id: 'codeList',
            value: codeList,
          },
        ],
        additionalContent: elementToString(node),
        skill: '',
        instructions: '',
        script: '',
      });
    } else if (node.classList.contains('activity-block')) {
      parseActivityBlock(node);
    } else if (node.tagName === 'HR') {
      // Skip horizontal rules
      return;
    } else if (node.tagName === 'H2') {
      // Handle section headings (Review/New Content)
      sections.push({
        id: uuidv4(),
        type: 'custom',
        title: node.textContent || '',
        description: elementToString(node),
        content: [],
        additionalContent: '',
        skill: '',
        instructions: '',
        script: '',
      });
    } else if (node.classList.contains('column-layout')) {
      // Extract activity blocks from within column layouts
      const activityBlocks = node.querySelectorAll('.activity-block');
      if (activityBlocks.length > 0) {
        activityBlocks.forEach((block) =>
          parseActivityBlock(block as HTMLElement),
        );
      } else {
        // Preserve non-activity column layouts as custom sections
        const content = cleanHtmlContent(node.textContent || '');
        if (content.trim()) {
          sections.push({
            id: uuidv4(),
            type: 'custom',
            title: '',
            description: elementToString(node),
            content: [],
            additionalContent: '',
            skill: '',
            instructions: '',
            script: '',
          });
        }
      }
    } else {
      // Handle any other custom HTML content
      const content = cleanHtmlContent(node.textContent || '');
      if (content.trim()) {
        // Only add non-empty sections
        sections.push({
          id: uuidv4(),
          type: 'custom',
          title: '',
          description: elementToString(node),
          content: [],
          additionalContent: '',
          skill: '',
          instructions: '',
          script: '',
        });
      }
    }
  }

  doc.body.childNodes.forEach(processNode);

  return sections;
}

// Updated Section type to include lessonGuidance
export type SectionType = ActivityType | 'custom' | 'lessonGuidance';

export interface Section {
  id: string | null;
  type: SectionType;
  title: string;
  description: string;
  content: ContentItem[];
  additionalContent: string;
  skill?: string;
  instructions?: string;
  script?: string;
}

export function generateSectionHTML(type: ActivityType): string {
  const id = uuidv4();
  const instructions = getActivityInstruction(type);

  const commonHeader = `
    <div class="activity-block" data-type="${type}" data-id="${id}">
      <h3>${escapeHtml(getFormattedActivity(type))}</h3>
      <p class="skill"><strong>Skill:</strong> ${escapeHtml(
        getActivitySkill(type).split(': ')[1],
      )}</p>
      <div class="instructions">
        <strong>Instructions:</strong>
        <ul>
          ${instructions
            .map((instruction) => `<li>${escapeHtml(instruction)}</li>`)
            .join('')}
        </ul>
      </div>
      <p class="script"><strong>Script:</strong> <em>"${escapeHtml(
        getActivityScript(type),
      )}"</em></p>`;

  if (type === ActivityType.WORD_MATRIX) {
    return `${commonHeader}
      <div class="word-matrix-content" data-base-word="" data-prefix-columns="" data-suffix-columns="">
        <div class="word-matrix-prefix-col"></div>
        <div class="word-matrix-base"></div>
        <div class="word-matrix-suffix-col"></div>
      </div>
      <p class="additional-notes"><strong>Additional Notes:</strong></p>
      <p class="additional-notes"></p>
    </div>
  `;
  }

  return `${commonHeader}
      <p class="activity-content"></p>
      <p class="additional-notes"><strong>Additional Notes:</strong></p>
      <p class="additional-notes"></p>
    </div>
  `;
}

export function addContentItemToHTML(
  html: string,
  sectionId: string,
  contentItem: ContentItem,
): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const section = doc.querySelector(`[data-id="${sectionId}"]`);
  if (section) {
    const contentContainer = section.querySelector('.activity-content');
    if (contentContainer) {
      const newItem = document.createElement('span');
      newItem.className = 'content-item';

      const rawValue = Array.isArray(contentItem.value)
        ? contentItem.value.join(', ')
        : contentItem.value;

      // Sound recognition items encode pronunciation as spelling|pronunciation
      const sectionType = section.getAttribute('data-type');
      if (
        sectionType === 'soundRecognitionDrill' &&
        typeof rawValue === 'string'
      ) {
        const pipeIdx = rawValue.indexOf('|');
        if (pipeIdx >= 0) {
          const spelling = rawValue.substring(0, pipeIdx);
          const pronunciation = rawValue.substring(pipeIdx + 1);
          const exampleWord = lookupExampleWord(spelling, pronunciation);
          newItem.textContent = exampleWord
            ? `${spelling} for ${exampleWord}`
            : spelling;
          newItem.setAttribute('data-pronunciation', pronunciation);
        } else {
          newItem.textContent = rawValue;
        }
      } else {
        newItem.textContent = rawValue;
      }

      contentContainer.appendChild(newItem);
    }
  }

  return doc.body.innerHTML;
}
export function getSelectedSectionInfo(
  content: string,
  selectedSectionId: string | null,
) {
  if (!selectedSectionId) return null;

  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const sectionElement = doc.querySelector(`[data-id="${selectedSectionId}"]`);

  if (!sectionElement) return null;

  const type = sectionElement.getAttribute('data-type');

  if (!type) return null;

  return {
    id: selectedSectionId,
    type: type as ActivityType,
  };
}

export function cleanHtmlContent(html: string): string {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
}

/** Look up the example word for a spelling + pronunciation pair. */
export function lookupExampleWord(
  spelling: string,
  pronunciation?: string,
): string | undefined {
  const graphemes = getGraphemesBySpelling(spelling);
  const grapheme = pronunciation
    ? graphemes.find((g) => g.pronunciation === pronunciation)
    : graphemes[0];
  return grapheme?.example
    ? getWordById(grapheme.example)?.spelling
    : undefined;
}

/**
 * Format a sound recognition value (spelling|pronunciation) for display.
 * Returns e.g. "igh for night".
 */
export function formatSoundRecognitionValue(raw: string): string {
  const pipeIdx = raw.indexOf('|');
  const spelling = pipeIdx >= 0 ? raw.substring(0, pipeIdx) : raw;
  const pronunciation = pipeIdx >= 0 ? raw.substring(pipeIdx + 1) : undefined;
  const exampleWord = lookupExampleWord(spelling, pronunciation);
  return exampleWord ? `${spelling} for ${exampleWord}` : spelling;
}

export interface WordMatrixData {
  baseWord: string;
  prefixColumns: string[][];
  suffixColumns: string[][];
}

function parsePipeDelimitedColumns(value: string): string[][] {
  if (!value) return [[]];
  return value.split('|').map((col) => col.split(',').filter(Boolean));
}

export function getWordMatrixDataFromHTML(
  html: string,
  sectionId: string,
): WordMatrixData {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const section = doc.querySelector(`[data-id="${sectionId}"]`);
  const container = section?.querySelector('.word-matrix-content');

  if (!container)
    return { baseWord: '', prefixColumns: [[]], suffixColumns: [[]] };

  // Read from data attributes first (TipTap serializes these)
  const attrBaseWord = container.getAttribute('data-base-word');
  const attrPrefixCols = container.getAttribute('data-prefix-columns');
  const attrSuffixCols = container.getAttribute('data-suffix-columns');

  if (
    attrBaseWord !== null ||
    attrPrefixCols !== null ||
    attrSuffixCols !== null
  ) {
    return {
      baseWord: attrBaseWord || '',
      prefixColumns: parsePipeDelimitedColumns(attrPrefixCols || ''),
      suffixColumns: parsePipeDelimitedColumns(attrSuffixCols || ''),
    };
  }

  // Fall back to legacy data-prefixes/data-suffixes attrs
  const legacyPrefixes = container.getAttribute('data-prefixes');
  const legacySuffixes = container.getAttribute('data-suffixes');
  if (legacyPrefixes !== null || legacySuffixes !== null) {
    return {
      baseWord: container.getAttribute('data-base-word') || '',
      prefixColumns: [
        legacyPrefixes ? legacyPrefixes.split(',').filter(Boolean) : [],
      ],
      suffixColumns: [
        legacySuffixes ? legacySuffixes.split(',').filter(Boolean) : [],
      ],
    };
  }

  // Fall back to child element text content
  const baseEl =
    container.querySelector('.word-matrix-base') ||
    container.querySelector('.base-word');
  const baseWord = baseEl?.textContent || '';

  const prefixCols = Array.from(
    container.querySelectorAll('.word-matrix-prefix-col'),
  );
  const prefixColumns =
    prefixCols.length > 0
      ? prefixCols.map((col) =>
          Array.from(col.querySelectorAll('.prefix')).map(
            (el) => el.textContent || '',
          ),
        )
      : [[]];

  const suffixCols = Array.from(
    container.querySelectorAll('.word-matrix-suffix-col'),
  );
  const suffixColumns =
    suffixCols.length > 0
      ? suffixCols.map((col) =>
          Array.from(col.querySelectorAll('.suffix')).map(
            (el) => el.textContent || '',
          ),
        )
      : [[]];

  return { baseWord, prefixColumns, suffixColumns };
}

function serializeColumns(columns: string[][]): string {
  return columns.map((col) => col.join(',')).join('|');
}

export function updateWordMatrixHTML(
  html: string,
  sectionId: string,
  data: WordMatrixData,
): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const section = doc.querySelector(`[data-id="${sectionId}"]`);
  const container = section?.querySelector('.word-matrix-content');

  if (!container) return html;

  // Set data attributes (TipTap WordMatrixContent extension reads these)
  container.setAttribute('data-base-word', data.baseWord);
  container.setAttribute(
    'data-prefix-columns',
    serializeColumns(data.prefixColumns),
  );
  container.setAttribute(
    'data-suffix-columns',
    serializeColumns(data.suffixColumns),
  );
  // Remove legacy attrs
  container.removeAttribute('data-prefixes');
  container.removeAttribute('data-suffixes');

  // Also rebuild child elements for non-TipTap consumers (stimulus sheet, presenter)
  const prefixColsHtml = data.prefixColumns
    .map((col) => {
      const items = col
        .map((p) => `<span class="prefix">${escapeHtml(p)}</span>`)
        .join('');
      return `<div class="word-matrix-prefix-col">${items}</div>`;
    })
    .join('');
  const baseWordHtml = `<div class="word-matrix-base">${escapeHtml(data.baseWord)}</div>`;
  const suffixColsHtml = data.suffixColumns
    .map((col) => {
      const items = col
        .map((s) => `<span class="suffix">${escapeHtml(s)}</span>`)
        .join('');
      return `<div class="word-matrix-suffix-col">${items}</div>`;
    })
    .join('');

  container.innerHTML = prefixColsHtml + baseWordHtml + suffixColsHtml;

  return doc.body.innerHTML;
}
